<template>
  <div
    class="mobileCardWrapper"
    v-if="item"
  >
    <div class="header">
      <div class="headerContent">
        <span class="headerTitle">{{ item?.dispositif }}</span>
        <span class="headerSubtitle">Brouillon {{ item?.id }}</span>
        <BasicBadge
          :type="StatutBadgeType[item.statut] as BadgeType"
          size="medium"
          :deletable="false"
          :label="StatutLibelle[item.statut]"
        />
      </div>
      <div class="actionsWrapper">
        <BasicTooltip
          v-if="stagiairesList.length || prestatairesList.length"
          label="Afficher le détail de la demande"
        >
          <div
            class="elem elem--span-1 elem--clickable"
            v-focusable="{
              onEnter: () => openStagiaireOrPrestataire,
            }"
            @click="openStagiaireOrPrestataire"
          >
            <BasicText size="h3">
              <div class="text">
                <span>...</span>
              </div>
            </BasicText>
          </div>
        </BasicTooltip>
        <BasicTooltip
          v-focusable="{
            onEnter: () => ouvrirDemande(item.dispositif, item.id),
          }"
          :label="readonly ? 'Consulter la demande' : 'Modifier la demande'"
        >
          <BasicIcon
            :name="readonly ? 'eye' : 'edit'"
            :active="false"
            @click="ouvrirDemande(item.dispositif, item.id)"
          />
        </BasicTooltip>
        <BasicTooltip
          v-if="checkClonePermission(item.dispositif)"
          v-focusable="{ onEnter: clickClone }"
          label="Dupliquer la demande"
        >
          <BasicIcon
            name="duplicate"
            :active="false"
            @click="clickClone"
          />
        </BasicTooltip>
        <BasicTooltip
          v-if="deletable"
          v-focusable="{
            onEnter: () => deleteDemande(item.dispositif, item.id),
          }"
          label="Supprimer la demande"
        >
          <BasicIcon
            name="trash"
            :active="false"
            @click="deleteDemande(item.dispositif, item.id)"
          />
        </BasicTooltip>
      </div>
    </div>

    <!-- CONTENT -->
    <div class="content">
      <div class="contentElement">
        <BasicIcon
          name="document-text"
          :active="false"
        />
        <span>{{ item?.dispositif }}</span>
      </div>
      <div class="contentElement">
        <BasicIcon
          name="teacher"
          :active="false"
        />
        <span>{{ item?.intitule }}</span>
      </div>
      <div class="contentElement">
        <BasicIcon
          name="bank"
          :active="false"
        />
        <span>{{ item?.adherent?.denomination }}</span>
      </div>
      <div
        class="contentElement"
        v-if="item.estSousMandatGestion && appState.contextCourant === 2"
      >
        <BasicIcon
          name="archive-book"
          active
        />
        <span>Mandat de gestion</span>
      </div>
      <div
        class="contentElement"
        v-if="item.avecSubrogation"
      >
        <BasicIcon
          name="wallet"
          :active="false"
        />
        <span>Subrogation de paiement</span>
      </div>
    </div>

    <div class="footer footer--grid">
      <div class="footerElem">
        <span class="footerTitle">Début</span>
        <span class="footerSubtitle">{{ dateFormatter(item.dateFormation?.start) }}</span>
      </div>
      <div class="footerElem">
        <span class="footerTitle">Fin</span>
        <span class="footerSubtitle">{{ dateFormatter(item.dateFormation?.end) }}</span>
      </div>
      <div class="footerElem">
        <span class="footerTitle">Stagiaire(s)</span>
        <BasicText nb-max-lines="2">
          <span
            v-if="stagiaires"
            class="footerSubtitle footerSubtitle--clickable"
            @click="showModalStagiaire = true"
          >
            {{ stagiaires }}
          </span>
          <span
            v-else
            class="footerSubtitle"
          >
            -
          </span>
        </BasicText>
      </div>
      <div class="footerElem">
        <span class="footerTitle">Organisme(s)</span>
        <BasicText nb-max-lines="2">
          <span
            v-if="prestataires"
            class="footerSubtitle footerSubtitle--clickable"
            @click="showModalOrganisme = true"
          >
            {{ prestataires }}
          </span>
          <span
            v-else
            class="footerSubtitle"
          >
            -
          </span>
        </BasicText>
      </div>
    </div>
  </div>

  <Teleport to="main">
    <ModalComponent v-model="showModalStagiaire">
      <template #header>Liste des stagiaires</template>
      <template #content>
        <BasicText
          v-for="(stagiaire, id) in stagiairesList"
          :key="id"
          color="neutral-500"
          size="body-m"
        >
          {{ stagiaire }}
        </BasicText>
      </template>
    </ModalComponent>
    <ModalComponent v-model="showModalOrganisme">
      <template #header>Liste des organismes</template>
      <template #content>
        <BasicText
          v-for="(organisme, id) in prestatairesList"
          :key="id"
          color="neutral-500"
          size="body-m"
        >
          {{ organisme }}
        </BasicText>
      </template>
    </ModalComponent>
  </Teleport>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import type { BadgeType } from '@designSystem/components'
  import ModalComponent from '@/features/interface/modal/ModalComponent.vue'
  import type { RechercheResultDTO } from '@/features/api/extranet/dto/Demande/Recherche'
  import { useDemandeHelper } from './../useDemandeHelper'
  import { dateFormatter } from '@/features/shared/tools/date'
  import { useRechercheDemandeActions } from '../useRechercheDemandeActions'
  import { StatutLibelle, StatutBadgeType } from '@/features/domaine/types/opco'
  import { useAppStateStore } from '@/features/application'

  const { cloneDemande, deleteDemande, ouvrirDemande } = useRechercheDemandeActions()
  const appState = useAppStateStore()

  const props = defineProps<{ item: RechercheResultDTO }>()
  const { deletable, readonly, stagiaires, prestataires, stagiairesList, prestatairesList, checkClonePermission } =
    useDemandeHelper(props.item)

  const showModalStagiaire = ref(false)
  const showModalOrganisme = ref(false)

  const openStagiaireOrPrestataire = () => {
    if (stagiairesList.value.length && !prestatairesList.value.length) {
      showModalStagiaire.value = true
    } else if (prestatairesList.value.length && !stagiairesList.value.length) {
      showModalOrganisme.value = true
    } else {
      showModalStagiaire.value = true
    }
  }

  const clickClone = () => {
    return cloneDemande({
      dispositif: props.item.dispositif,
      idDemande: props.item.id,
      idcc: props.item.adherent.idcc!.code,
      siret: props.item.adherent.siret.valeur!,
      permissions: props.item.niveauPermission,
    })
  }
</script>

<style scoped>
  @import '../../index.less';
  @import './DemandeCardMobile.less';
</style>
